import moment from 'moment';
import { DensityLink } from '../../density-types';

import {
  CaptureScheduleModalState,
  DaysOfWeek,
} from '../../types/capture-schedule-modal';

import { CaptureScheduleModalSensor } from '../../components/CaptureScheduleModal';

export const TWENTY_FOUR_HOURS_IN_SECONDS = 24 * 60 * 60;
export const CAPTURE_WARNING_THRESHOLD = 20;

export type CaptureScheduleRequest = {
  organizationId: string;
  body: {
    link_ids: Array<DensityLink['id']>;
    days_of_week: Array<DaysOfWeek>;
    time: string;
    end_date: string;
    num_event_retries: number;
    num_captures: number;
    duration: number;
  };
};

export function parseCapturesPerSchedule(
  state: CaptureScheduleModalState,
): number {
  return parseInt(state.capturesPerSchedule, 10);
}

export function parseCaptureDurationInSeconds(
  state: CaptureScheduleModalState,
): number {
  return parseInt(state.captureDurationInSeconds, 10);
}

export function calculateEndDate(
  modalData: CaptureScheduleModalState,
  now: moment.Moment = moment.utc(),
): moment.Moment {
  switch (modalData.duration.type) {
    case 'TOTAL_CAPTURES': {
      const totalCaptures = parseInt(modalData.duration.value, 10);
      const capturesPerSchedule = parseCapturesPerSchedule(modalData);
      const captureDurationInSeconds = parseCaptureDurationInSeconds(modalData);

      if (modalData.schedules.length === 0 || isNaN(totalCaptures)) {
        return now;
      }

      // Aggregate through each day, adding captures until the total number has been reached.
      let captures = 0;
      let date = now.clone().startOf('day');
      while (true) {
        const schedulesOnDay = modalData.schedules.filter((s) =>
          s.daysOfWeek.includes(date.format('dddd') as DaysOfWeek),
        );
        captures += schedulesOnDay.length * capturesPerSchedule;
        // Check to see if we've gone past the number of captures that we have been allotted
        if (captures >= totalCaptures) {
          // If so, return the date plus the length of the current capture
          return date.endOf('day');
        }
        // None of the schedules today pushed the number of captures above the threshold, so increment to the
        // next day and try again.
        date.add(1, 'day');
      }
    }
    case 'END_DATE':
      return modalData.duration.value;
  }
}

export function calculateTotalCaptures(
  modalData: CaptureScheduleModalState,
  now: moment.Moment = moment.utc(),
): number {
  switch (modalData.duration.type) {
    case 'TOTAL_CAPTURES':
      return parseInt(modalData.duration.value, 10);
    case 'END_DATE': {
      const endDate = modalData.duration.value;
      const capturesPerSchedule = parseCapturesPerSchedule(modalData);
      const captureDurationInSeconds = parseCaptureDurationInSeconds(modalData);

      if (modalData.schedules.length === 0) {
        return 0;
      }

      // Aggregate through each day, adding captures until the end date has been reached.
      let captures = 0;
      for (let date = now.clone(); date.isBefore(endDate); date.add(1, 'day')) {
        const schedulesOnDay = modalData.schedules.filter((s) =>
          s.daysOfWeek.includes(date.format('dddd') as DaysOfWeek),
        );
        captures += schedulesOnDay.length * capturesPerSchedule;
      }
      return captures;
    }
  }
}

export function convertModalDataToCaptureSchedules(
  modalData: CaptureScheduleModalState,
  sensors: Array<CaptureScheduleModalSensor>,
  now: moment.Moment = moment.utc(),
): Array<CaptureScheduleRequest> {
  const endDate = calculateEndDate(modalData, now);
  const capturesPerSchedule = parseCapturesPerSchedule(modalData);
  const captureDurationInSeconds = parseCaptureDurationInSeconds(modalData);

  // Group sensors into buckets by their organization
  const organizationGroups: { [id: string]: Array<any> } = sensors.reduce(
    (groups, sensor) => {
      const key = sensor.organization.id;
      if (groups[key]) {
        return { ...groups, [key]: [...groups[key], sensor] };
      } else {
        return { ...groups, [key]: [sensor] };
      }
    },
    {},
  );

  // Make a request to create capture schedules for every organization and every schedule created.
  return Object.entries(organizationGroups).flatMap(
    ([organizationId, sensors]) => {
      return modalData.schedules.map((schedule) => {
        return {
          organizationId: organizationId,
          body: {
            link_ids: sensors.map((sensor) => sensor.link_id),
            days_of_week: schedule.daysOfWeek,
            time: schedule.time,
            end_date: endDate.format(),
            num_event_retries: schedule.numberOfEventRetries,
            num_captures: capturesPerSchedule,
            duration: captureDurationInSeconds,
          },
        };
      });
    },
  );
}
